import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MedicationIcon from '@mui/icons-material/Medication';
import AccessibleIcon from '@mui/icons-material/Accessible';
import QuizIcon from '@mui/icons-material/Quiz';
import {
  CircularProgress,
  Fade,
  Paper,
  Tab,
  Tabs,
  useTheme,
  FormControlLabel,
  Switch
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import Stack from '@mui/material/Stack';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import RatingButtons from './RatingButtons';
import apiService from '../providers/ApiService';
// Custom tab panel component for content switching
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`case-tabpanel-${index}`}
      aria-labelledby={`case-tab-${index}`}
      {...other}
      style={{ height: '100%' }}>
      {value === index && <Fade in={true}>{children}</Fade>}
    </div>
  );
}

// PropTypes for TabPanel component
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

const items = [
  { icon: <MedicationIcon />, title: 'Doctor', index: 'doctor' },
  { icon: <AccessibleIcon />, title: 'Patient Notes', index: 'patient' },
  { icon: <QuizIcon />, title: 'Marking', index: 'marking' },
  { icon: <WorkIcon />, title: 'Management', index: 'management' }
];

// Helper: Convert camelCase or snake_case to Title Case
const formatTitle = (str) => {
  const exceptions = ['of', 'to', 'and', 'in'];
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .split(' ')
    .map((word, index) =>
      exceptions.includes(word.toLowerCase()) && index !== 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
};

// Section field orders
const sectionOrders = {
  marking: [
    'positiveIndicatorsRelating',
    'negativeIndicatorsRelating',
    'positiveIndicatorsManagement',
    'negativeIndicatorsManagement',
    'positiveIndicatorsGathering',
    'negativeIndicatorsGathering'
  ],
  patient: [
    'name',
    'age',
    'presentingComplaint',
    'ideas',
    'concerns',
    'expectations',
    'positiveSX',
    'negativeSX',
    'negativeSymptoms',
    'openHistory',
    'socialHistory',
    'familyHistory'
  ],
  management: [
    'adviceToPatients',
    'safetyNet',
    'managementOfCase',
    'managementOfDisease',
    'relation',
    'furtherReading'
  ],
  doctor: [
    'image',
    'name',
    'age',
    'PMHX',
    'medicationHistory',
    'caseDetails',
    'results',
    'medicalNotes'
  ]
};

const resolveValue = (caseData, index) => {
  if (!index || index === '*') {
    // Show the entire object as JSON
    return JSON.stringify(caseData, null, 2);
  }

  if (Array.isArray(index)) {
    for (const path of index) {
      const value = path.split('.').reduce((obj, key) => (obj ? obj[key] : undefined), caseData);
      if (value) return renderValue(path, value); // Pass to custom render helper
    }
    return 'No data available for the provided paths.';
  }

  const value = index.split('.').reduce((obj, key) => (obj ? obj[key] : undefined), caseData);
  return renderValue(index, value); // Pass to custom render helper
};

// Main renderValue function
const renderValue = (key, value) => {
  // Styling constants
  const styles = {
    subHeading: { fontWeight: 'bold', fontSize: '1.25rem', mb: 1 },
    fieldValue: { ml: 2, fontSize: '0.95rem', whiteSpace: 'pre-wrap' },
    positive: { color: 'green', mb: 1, fontSize: '1.25rem' },
    negative: { color: 'red', mb: 1, fontSize: '1.25rem' },
    generalField: { fontWeight: 'bold', mb: 1 }
  };

  // Render sections with ordering
  if (sectionOrders[key]) {
    const order = sectionOrders[key];
    return (
      <Box>
        {/* Ordered fields */}
        {order.map((orderedKey) => {
          if (value[orderedKey]) {
            return (
              <Box key={orderedKey} sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  sx={
                    orderedKey.includes('positive')
                      ? styles.positive
                      : orderedKey.includes('negative')
                        ? styles.negative
                        : styles.subHeading
                  }>
                  {formatTitle(orderedKey)}:
                </Typography>
                {orderedKey === 'image' ? (
                  <Box
                    sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <img
                      src={value[orderedKey]}
                      alt="Case"
                      style={{
                        maxWidth: '70%',
                        borderRadius: 12,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Source: {value[orderedKey]}
                    </Typography>
                  </Box>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      ml: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {value[orderedKey]}
                    </Typography>
                  </Paper>
                )}
              </Box>
            );
          }
          return null;
        })}
        {/* Unordered fields */}
        {Object.keys(value)
          .filter((fieldKey) => !order.includes(fieldKey))
          .map((fieldKey) => (
            <Box key={fieldKey} sx={{ mb: 3 }}>
              <Typography variant="body1" sx={styles.subHeading}>
                {formatTitle(fieldKey)}:
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  ml: 2,
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {value[fieldKey]}
                </Typography>
              </Paper>
            </Box>
          ))}
      </Box>
    );
  }

  // Default rendering for other fields
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', ...styles.fieldValue }}>
      {value || 'No data available'}
    </Typography>
  );
};

export default function CaseView() {
  const theme = useTheme();
  const { state } = useLocation();
  if (!state || !state.caseIds || state.caseIds.length === 0) {
    window.location.href = '/categories';
  }
  const [caseData, setCaseData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);
  const [currentIndex, setCurrentIndex] = React.useState(state?.index || 0);
  const [showAnonymous, setShowAnonymous] = React.useState(state?.showAnonymous || false);
  const caseIds = state?.caseIds || [];
  const hasExpired = state?.hasExpired;

  // Create a style object to prevent text selection
  const noSelectStyle = {
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    userSelect: 'none'
  };

  useEffect(() => {
    if (caseIds.length > 0) {
      fetchCase(caseIds[currentIndex]);
    }
  }, [currentIndex, caseIds]);

  // Add event listener to prevent copy events
  useEffect(() => {
    const preventCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Add listeners for various copy-related events
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);

    // Cleanup listeners on component unmount
    return () => {
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
    };
  }, []);

  const isLoggedIn = localStorage.getItem('accessToken');
  if (!isLoggedIn) {
    window.location.href = '/sign-in';
  }

  if (hasExpired) {
    window.location.href = '/tiers';
  }

  const fetchCase = async (caseId) => {
    try {
      const data = await apiService.get(`/cases/${caseId}`);
      setCaseData(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch case data', error);
      setLoading(false);
    }
  };

  const handleForwardClick = () => {
    if (currentIndex < caseIds.length - 1) {
      setLoading(true);
      setCurrentIndex((prevIndex) => prevIndex + 1);
      // Anonymous mode is maintained via React state, no need to update it here
    }
  };

  const handleBackClick = () => {
    if (currentIndex > 0) {
      setLoading(true);
      setCurrentIndex((prevIndex) => prevIndex - 1);
      // Anonymous mode is maintained via React state, no need to update it here
    }
  };

  const handleItemClick = (index) => setSelectedItemIndex(index);

  // Function to safely get the title based on anonymous mode
  const getTitle = () => {
    if (!caseData) return '';
    if (showAnonymous && caseData.anonymousTitle) {
      return caseData.anonymousTitle;
    }
    return caseData.title;
  };

  if (loading) {
    return (
      <Container
        sx={{
          textAlign: 'center',
          mt: 5,
          pt: { xs: 12, sm: 12 },
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 500 }}>
          Loading case...
        </Typography>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        pt: { xs: 8, md: 10 },
        pb: 5,
        ...noSelectStyle
      }}>
      <Container maxWidth="xl">
        {/* Case navigation indicators */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            pt: 3
          }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Case {currentIndex + 1} of {caseIds.length}
            </Typography>
            {state?.categoryName && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                Category: {state.categoryName}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Button
              onClick={handleBackClick}
              disabled={currentIndex === 0}
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIosIcon fontSize="small" />}
              sx={{ borderRadius: 2 }}>
              Previous
            </Button>
            <Button
              onClick={handleForwardClick}
              disabled={currentIndex === caseIds.length - 1}
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIosIcon fontSize="small" />}
              sx={{ borderRadius: 2 }}>
              Next
            </Button>
          </Stack>
        </Box>

        <Paper
          elevation={2}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            mb: 4
          }}>
          {/* Header section */}
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              background:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(to right, #1a237e, #283593)'
                  : 'linear-gradient(to right, #e3f2fd, #bbdefb)'
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAnonymous}
                    onChange={(e) => setShowAnonymous(e.target.checked)}
                    color="primary"
                  />
                }
                label="Anonymous Mode"
              />
            </Box>
            <Typography
              component="h1"
              variant="h4"
              color="text.primary"
              textAlign="center"
              sx={{ fontWeight: 600 }}>
              {getTitle()}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 1 }}>
              {caseData.patient.name}, Age {caseData.patient.age}
            </Typography>
          </Box>

          {/* Main content */}
          <Grid container>
            {/* Tabs - Desktop */}
            <Grid
              item
              xs={12}
              sx={{
                display: { xs: 'none', md: 'block' },
                borderBottom: 1,
                borderColor: 'divider'
              }}>
              <Tabs
                value={selectedItemIndex}
                onChange={(_, index) => handleItemClick(index)}
                variant="fullWidth"
                aria-label="Case data tabs"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 64,
                    textTransform: 'none',
                    fontSize: '1rem'
                  }
                }}>
                {items.map((item, index) => (
                  <Tab
                    key={index}
                    icon={item.icon}
                    iconPosition="start"
                    label={item.title}
                    id={`case-tab-${index}`}
                    aria-controls={`case-tabpanel-${index}`}
                  />
                ))}
              </Tabs>
            </Grid>

            {/* Mobile tab selection */}
            <Grid
              item
              xs={12}
              sx={{
                display: { xs: 'block', md: 'none' },
                p: 1.5,
                borderBottom: 1,
                borderColor: 'divider'
              }}>
              <Box
                sx={{
                  display: 'flex',
                  overflowX: 'auto',
                  pb: 1,
                  '&::-webkit-scrollbar': { height: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2 }
                }}>
                {items.map((item, index) => (
                  <Paper
                    key={index}
                    onClick={() => handleItemClick(index)}
                    elevation={selectedItemIndex === index ? 2 : 0}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      py: 1.5,
                      mr: 1,
                      minWidth: 'fit-content',
                      borderRadius: 2,
                      cursor: 'pointer',
                      backgroundColor:
                        selectedItemIndex === index ? 'primary.main' : 'background.paper',
                      color: selectedItemIndex === index ? 'primary.contrastText' : 'text.primary',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap'
                    }}>
                    <Box sx={{ mr: 1, display: 'flex' }}>{item.icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.title}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Grid>

            {/* Content area */}
            <Grid item xs={12}>
              <Box
                sx={{
                  height: { xs: 'auto', md: 'calc(100vh - 320px)' },
                  minHeight: { xs: 400, md: 500 },
                  maxHeight: { xs: 'none', md: '80vh' },
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '4px'
                  }
                }}>
                {items.map((item, index) => (
                  <TabPanel key={index} value={selectedItemIndex} index={index}>
                    <Box sx={{ p: { xs: 2, md: 3 } }}>
                      {index === selectedItemIndex && resolveValue(caseData, item.index)}
                    </Box>
                  </TabPanel>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Rating and navigation bottom bar */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              gap: 2
            }}>
            <RatingButtons caseId={caseIds[currentIndex]} />

            {/* Mobile navigation buttons */}
            <Stack
              direction="row"
              spacing={2}
              sx={{
                display: { xs: 'flex', md: 'none' },
                width: '100%'
              }}>
              <Button
                onClick={handleBackClick}
                disabled={currentIndex === 0}
                variant="outlined"
                fullWidth
                startIcon={<ArrowBackIosIcon />}
                sx={{ borderRadius: 2 }}>
                Previous
              </Button>
              <Button
                onClick={handleForwardClick}
                disabled={currentIndex === caseIds.length - 1}
                variant="contained"
                fullWidth
                endIcon={<ArrowForwardIosIcon />}
                sx={{ borderRadius: 2 }}>
                Next
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
